import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnChanges,
  SecurityContext,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Katex, loadKatex } from './katex-loader';
import { BOLD_PATTERN, hasMath, latexToPlain, looksLikeHtml, splitMath } from './math-text';

/**
 * Affiche un texte de question : sauts de ligne, gras, formules.
 *
 * Il remplace une interpolation `{{ texte }}` sans rien changer autour : le
 * composant est en ligne, hérite de la police, de la taille et de la couleur
 * de son parent, et ne pose aucune marge. Un énoncé sans formule s'affiche
 * comme avant, retours à la ligne en plus.
 *
 * Le texte n'est jamais injecté comme HTML : chaque fragment devient un nœud
 * texte, et seules les formules passent par KaTeX (sans `\href` ni commande
 * de confiance). L'exception est l'ancien HTML des descriptions, assaini par
 * Angular avant insertion.
 *
 * Une formule s'écrit toujours de gauche à droite, y compris au milieu d'un
 * énoncé arabe : elle est isolée du sens du texte qui l'entoure.
 */
@Component({
  selector: 'app-math-text',
  standalone: true,
  template: '',
  styleUrl: './math-text.component.css',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'math-text', '[class.math-text--inline]': 'inline' },
})
export class MathTextComponent implements OnChanges {
  @Input() text: string | null | undefined = '';

  /**
   * Rendu sur une ligne, pour les listes compactes (titre tronqué, ligne de
   * tableau) : les retours à la ligne se fondent en espaces et une formule
   * centrée reste dans la ligne. Le texte complet se lit à l'ouverture.
   */
  @Input() inline = false;

  /** Résolu une fois le rendu définitif posé — formules comprises. */
  rendered: Promise<void> = Promise.resolve();

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly sanitizer = inject(DomSanitizer);
  private renderId = 0;

  ngOnChanges(): void {
    this.rendered = this.render();
  }

  private async render(): Promise<void> {
    const id = ++this.renderId;
    const element = this.host.nativeElement;
    const text = this.text ?? '';
    const html = looksLikeHtml(text);
    const math = hasMath(text);

    element.classList.toggle('math-text--html', html && !this.inline);

    // Un parent qui conserve les espaces (pre-wrap) les garde : un énoncé
    // aligné par des espaces, une cellule indentée s'affichent comme avant.
    const parent = element.parentElement;
    const parentSpace = parent ? getComputedStyle(parent).whiteSpace : '';
    element.classList.toggle('math-text--preserve', /^(pre|pre-wrap|break-spaces)$/.test(parentSpace));

    // Premier rendu immédiat : formules en lecture à plat (« R₁ ») le temps
    // que KaTeX arrive. Il ne charge qu'une fois ; ensuite le rendu est direct.
    this.paint(text, html, null);
    if (!math) return;

    let katex: Katex;
    try {
      katex = await loadKatex();
    } catch {
      return; // la lecture à plat reste affichée
    }

    if (id !== this.renderId) return; // un texte plus récent a pris la place
    this.paint(text, html, katex);
  }

  private paint(text: string, html: boolean, katex: Katex | null): void {
    const element = this.host.nativeElement;

    if (html) {
      const safe = this.sanitizer.sanitize(SecurityContext.HTML, text) ?? '';

      // Liste compacte : les paragraphes de l'ancien HTML feraient grandir la
      // ligne et perdre sa troncature. On n'en garde que le texte.
      if (this.inline) {
        const flat = new DOMParser().parseFromString(safe, 'text/html').body.textContent ?? '';
        element.replaceChildren(buildFragment(flat, katex, true));
        return;
      }

      element.innerHTML = safe;
      replaceMathInTextNodes(element, katex, false);
      return;
    }

    element.replaceChildren(buildFragment(text, katex, this.inline));
  }
}

/** Texte → nœuds : fragments de texte (gras compris) et formules. */
export function buildFragment(text: string, katex: Katex | null, inline = false): DocumentFragment {
  const fragment = document.createDocumentFragment();

  for (const segment of splitMath(text)) {
    if (!segment.math) {
      appendText(fragment, segment.value);
      continue;
    }

    const display = segment.display && !inline;
    const span = document.createElement('span');
    span.className = display ? 'math-text__math math-text__math--display' : 'math-text__math';
    span.dir = 'ltr';

    if (katex) {
      katex.render(segment.value, span, {
        displayMode: display,
        throwOnError: false,
        strict: 'ignore',
        trust: false,
        maxExpand: 500,
        maxSize: 20,
        output: 'htmlAndMathml',
      });
    } else {
      span.textContent = latexToPlain(segment.value);
    }

    fragment.append(span);
  }

  return fragment;
}

/** Texte simple, avec le gras `**…**` rendu en `<strong>`. */
function appendText(parent: Node, text: string): void {
  const pattern = new RegExp(BOLD_PATTERN.source, 'g');
  let last = 0;

  for (const match of text.matchAll(pattern)) {
    if (match.index! > last) parent.appendChild(document.createTextNode(text.slice(last, match.index)));
    const strong = document.createElement('strong');
    strong.textContent = match[1];
    parent.appendChild(strong);
    last = match.index! + match[0].length;
  }

  if (last < text.length) parent.appendChild(document.createTextNode(text.slice(last)));
}

/** Dans un HTML déjà assaini, remplace les formules portées par les nœuds texte. */
function replaceMathInTextNodes(root: HTMLElement, katex: Katex | null, inline: boolean): void {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const targets: Text[] = [];

  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (hasMath(node.nodeValue)) targets.push(node as Text);
  }

  for (const node of targets) node.replaceWith(buildFragment(node.nodeValue ?? '', katex, inline));
}
